import React, { useState, useEffect } from 'react';
import { useClient } from '../../contexts/ClientContext';
import { useProject } from '../../contexts/ProjectContext';
import { useFilter } from '../../contexts/FilterContext';
import { getTaggingData, createPolicy, updatePolicy, deletePolicy } from '../../services/cloudopsApi';
import { FiTag as Tag, FiAlertCircle as AlertCircle, FiCheckCircle as CheckCircle, FiTrendingUp as TrendingUp, FiDollarSign as DollarSign, FiPackage as Package, FiSettings as Settings, FiPlus as Plus, FiTrash2 as Trash2, FiX } from 'react-icons/fi';

interface TaggingPolicyData {
  id: string;
  name: string;
  description: string;
  requiredTags: string[];
  enforcementLevel: string;
  complianceRate: number;
  compliantResources: number;
  nonCompliantResources: number;
  validValues?: string[];
}

interface TagCoverage {
  tagKey: string;
  resourceCount: number;
  coverage: number;
}

interface ResourceByTag {
  tag: string;
  count: number;
  cost: number;
  services: string[];
}

interface UntaggedResource {
  resourceId: string;
  resourceType: string;
  region: string;
  cost: number;
  age: number;
}

interface TaggingDataState {
  compliance: {
    complianceRate: number;
    trend: number;
    taggedResources: number;
    totalResources: number;
    untaggedResources: number;
  };
  costAllocation: {
    allocationRate: number;
    allocatedCost: number;
    totalCost: number;
    unallocatedCost: number;
  };
  tagCoverage: TagCoverage[];
  policies: TaggingPolicyData[];
  resourcesByTag: ResourceByTag[];
  untaggedResources: UntaggedResource[];
}

interface NewPolicyForm {
  name: string;
  description: string;
  requiredTags: string;
  enforcementLevel: string;
}

const TaggingPage: React.FC = () => {
  const { selectedClientId } = useClient();
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<string>('compliance');
  const [taggingData, setTaggingData] = useState<TaggingDataState | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingPolicy, setEditingPolicy] = useState<TaggingPolicyData | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newPolicy, setNewPolicy] = useState<NewPolicyForm>({
    name: '',
    description: '',
    requiredTags: '',
    enforcementLevel: 'Mandatory'
  });
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    loadTaggingData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedProjectId, filterType, filterId]);

  const loadTaggingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getTaggingData();
      setTaggingData(data);
      setLoading(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load tagging data');
      setLoading(false);
    }
  };

  const handleSavePolicy = async () => {
    try {
      setSaving(true);

      // Call update API
      const updatedPolicy = await updatePolicy(editingPolicy!.id, {
        name: editingPolicy!.name,
        description: editingPolicy!.description,
        requiredTags: editingPolicy!.requiredTags,
        enforcementLevel: editingPolicy!.enforcementLevel
      });

      // Update the policy in local state
      setTaggingData({
        ...taggingData!,
        policies: taggingData!.policies.map(p =>
          p.id === updatedPolicy.id ? updatedPolicy : p
        )
      });

      // Close modal
      setShowEditModal(false);
      setEditingPolicy(null);
      setSaving(false);
    } catch (err: unknown) {
      alert(`Failed to update policy: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSaving(false);
    }
  };

  const handleCreatePolicy = async () => {
    try {
      setSaving(true);

      // Parse comma-separated tags into array
      const tagsArray = newPolicy.requiredTags
        .split(',')
        .map(t => t.trim())
        .filter(t => t);

      // Call create API
      const createdPolicy = await createPolicy({
        name: newPolicy.name,
        description: newPolicy.description,
        requiredTags: tagsArray,
        enforcementLevel: newPolicy.enforcementLevel
      });

      // Add new policy to local state
      setTaggingData({
        ...taggingData!,
        policies: [...taggingData!.policies, createdPolicy]
      });

      // Reset form and close modal
      setNewPolicy({
        name: '',
        description: '',
        requiredTags: '',
        enforcementLevel: 'Mandatory'
      });
      setShowCreateModal(false);
      setSaving(false);
    } catch (err: unknown) {
      alert(`Failed to create policy: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setSaving(false);
    }
  };

  const handleDeletePolicy = async (policyId: string, policyName: string) => {
    if (!globalThis.confirm(`Are you sure you want to delete the policy "${policyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Call delete API
      await deletePolicy(policyId);

      // Remove policy from local state
      setTaggingData({
        ...taggingData!,
        policies: taggingData!.policies.filter(p => p.id !== policyId)
      });
    } catch (err: unknown) {
      alert(`Failed to delete policy: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const getComplianceColor = (rate: number): string => {
    if (rate >= 90) return 'text-green-600 dark:text-green-400';
    if (rate >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <button
            onClick={loadTaggingData}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!taggingData) return null;

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Tag className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tagging Policies</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Cloud resource tagging compliance and cost allocation
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>New Policy</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Policy Compliance</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {taggingData.compliance.complianceRate}%
              </p>
              <div className="flex items-center mt-2">
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400 mr-1" />
                <span className="text-sm text-green-600 dark:text-green-400">+{taggingData.compliance.trend}%</span>
              </div>
            </div>
            <Tag className="w-12 h-12 text-blue-600 dark:text-blue-400 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Compliant Resources</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                {taggingData.compliance.taggedResources}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                of {taggingData.compliance.totalResources} total
              </p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Non-Compliant Resources</p>
              <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                {taggingData.compliance.untaggedResources}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Need attention
              </p>
            </div>
            <AlertCircle className="w-12 h-12 text-red-600 dark:text-red-400 opacity-20" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Cost Allocation</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                {taggingData.costAllocation.allocationRate}%
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                ${taggingData.costAllocation.allocatedCost.toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-12 h-12 text-purple-600 dark:text-purple-400 opacity-20" />
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 transition-colors">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setSelectedView('compliance')}
              className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                selectedView === 'compliance'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Compliance
            </button>
            <button
              onClick={() => setSelectedView('policies')}
              className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                selectedView === 'policies'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Policies
            </button>
            <button
              onClick={() => setSelectedView('costAllocation')}
              className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                selectedView === 'costAllocation'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Cost Allocation
            </button>
            <button
              onClick={() => setSelectedView('untagged')}
              className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                selectedView === 'untagged'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Untagged Resources
            </button>
          </div>
        </div>
      </div>

      {/* Compliance View */}
      {selectedView === 'compliance' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Tag Coverage by Key</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Percentage of scanned resources that have each tag key applied</p>
            <div className="space-y-4">
              {taggingData.tagCoverage.map((tag) => (
                <div key={tag.tagKey}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Tag className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{tag.tagKey}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {tag.resourceCount} resources
                      </span>
                      <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {tag.coverage}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-blue-500 dark:bg-blue-400"
                      style={{ width: `${tag.coverage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Policies View */}
      {selectedView === 'policies' && (
        <div className="space-y-4">
          {taggingData.policies.map((policy) => (
            <div key={policy.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{policy.name}</h3>
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        policy.enforcementLevel === 'Mandatory'
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      }`}
                    >
                      {policy.enforcementLevel}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{policy.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Required tags:</span>
                    {policy.requiredTags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  {policy.validValues && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Valid values:</span>
                      {policy.validValues.map((value) => (
                        <span
                          key={value}
                          className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setEditingPolicy(policy);
                      setShowEditModal(true);
                    }}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                    title="Edit policy"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeletePolicy(policy.id, policy.name)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Delete policy"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Compliance Rate</span>
                  <span className={`text-sm font-semibold ${getComplianceColor(policy.complianceRate)}`}>
                    {policy.complianceRate}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full ${
                      policy.complianceRate >= 90
                        ? 'bg-green-600'
                        : policy.complianceRate >= 75
                        ? 'bg-yellow-600'
                        : 'bg-red-600'
                    }`}
                    style={{ width: `${policy.complianceRate}%` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Compliant: </span>
                    <span className="font-semibold text-green-600 dark:text-green-400">
                      {policy.compliantResources}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Non-compliant: </span>
                    <span className="font-semibold text-red-600 dark:text-red-400">
                      {policy.nonCompliantResources}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cost Allocation View */}
      {selectedView === 'costAllocation' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cost by Tag</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Tag
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Resources
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Monthly Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Services
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {taggingData.resourcesByTag.map((item) => (
                    <tr key={item.tag} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                          {item.tag}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{item.count}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">
                        ${item.cost.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {item.services.map((service) => (
                            <span
                              key={service}
                              className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Cost Allocation Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Monthly Cost</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${taggingData.costAllocation.totalCost.toLocaleString()}
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Allocated Cost</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  ${taggingData.costAllocation.allocatedCost.toLocaleString()}
                </p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Unallocated Cost</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  ${taggingData.costAllocation.unallocatedCost.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Untagged Resources View */}
      {selectedView === 'untagged' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Resource ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Monthly Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Age (days)
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {taggingData.untaggedResources.map((resource, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 text-sm font-mono text-gray-900 dark:text-white">
                      {resource.resourceId}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Package className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">{resource.resourceType}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{resource.region}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">
                      ${resource.cost.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{resource.age} days</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        disabled
                        className="text-gray-400 dark:text-gray-600 text-sm font-medium cursor-not-allowed"
                        title="Tagging must be done in AWS Console"
                      >
                        Add Tags
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Policy Modal */}
      {showEditModal && editingPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Tagging Policy</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPolicy(null);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="tag-create-policy-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Policy Name
                </label>
                <input
                                                                                  id="tag-create-policy-name"
                  type="text"
                  value={editingPolicy.name}
                  onChange={(e) => setEditingPolicy({...editingPolicy, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="tag-policy-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                                                                                  id="tag-policy-description"
                  value={editingPolicy.description}
                  onChange={(e) => setEditingPolicy({...editingPolicy, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="edit-required-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Required Tags * (comma-separated)
                </label>
                <input id="edit-required-tags"
                  type="text"
                  value={editingPolicy.requiredTags.join(', ')}
                  onChange={(e) => setEditingPolicy({
                    ...editingPolicy,
                    requiredTags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                  })}
                  placeholder="e.g., Project, Environment, Owner"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter tag keys that must be present on all resources
                </p>
              </div>

              <div>
                <label htmlFor="tag-enforcement-level" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Enforcement Level
                </label>
                <select
                                                                                  id="tag-enforcement-level"
                  value={editingPolicy.enforcementLevel}
                  onChange={(e) => setEditingPolicy({...editingPolicy, enforcementLevel: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="Mandatory">Mandatory</option>
                  <option value="Warning">Warning</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingPolicy(null);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePolicy}
                  disabled={saving || !editingPolicy.requiredTags || editingPolicy.requiredTags.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Policy Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Create Tagging Policy</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPolicy({
                    name: '',
                    description: '',
                    requiredTags: '',
                    enforcementLevel: 'Mandatory'
                  });
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="tag-policy-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Policy Name *
                </label>
                <input
                                                                                  id="tag-policy-name"
                  type="text"
                  value={newPolicy.name}
                  onChange={(e) => setNewPolicy({...newPolicy, name: e.target.value})}
                  placeholder="e.g., Environment Tag Required"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="tag-policy-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                                                                                  id="tag-policy-description"
                  value={newPolicy.description}
                  onChange={(e) => setNewPolicy({...newPolicy, description: e.target.value})}
                  placeholder="Describe the purpose of this policy..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="new-required-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Required Tags * (comma-separated)
                </label>
                <input id="new-required-tags"
                  type="text"
                  value={newPolicy.requiredTags}
                  onChange={(e) => setNewPolicy({...newPolicy, requiredTags: e.target.value})}
                  placeholder="e.g., Environment, Owner, CostCenter"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter tag keys that must be present on all resources
                </p>
              </div>

              <div>
                <label htmlFor="tag-enforcement-level" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Enforcement Level
                </label>
                <select
                                                                                  id="tag-enforcement-level"
                  value={newPolicy.enforcementLevel}
                  onChange={(e) => setNewPolicy({...newPolicy, enforcementLevel: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="Mandatory">Mandatory</option>
                  <option value="Warning">Warning</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewPolicy({
                      name: '',
                      description: '',
                      requiredTags: '',
                      enforcementLevel: 'Mandatory'
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePolicy}
                  disabled={saving || !newPolicy.name || !newPolicy.requiredTags}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Creating...' : 'Create Policy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaggingPage;
